/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Howl } from "howler";
import * as Blockly from "blockly";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Scene } from "@babylonjs/core/scene";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { AppDispatch, RootState } from "app/store";
import { sleep, useScale, btnDelay, usePlaySound } from "common/utils";
import { Course } from "features/courses/types";
import { useGameSound } from "features/courses/hook";
import { ResetBtnIcon } from "features/courses/assets";
import { actions } from "features/courses/coursesSlice";
import { recordCourseLevelRun } from "features/courses/analytics";
import StageMaps, { checkBtnColor } from "features/courses/algorithm/game/scene/maps";
import { updateAlgorithmLevelAnswer, updateAlgorithmLevelProgresses } from "features/courses/api";
import {
  performMove,
  performJump,
  performTurnToLeft,
  performTurnToRight,
} from "features/courses/algorithm/game/scene/animations";
import {
  Score,
  Failed,
  offset,
  Success,
  StartBtn,
  ResetBtn,
  GameScene,
} from "features/courses/algorithm/game/scene/components";

export const MainScenePage = (props: {
  stage: number;
  step: number;
  reload: boolean;
  setReload: (reload: boolean) => void;
  isFinished: boolean;
  scene: Scene;
  gameBgm: Howl | null;
  gameStart: boolean;
  setGameStart: (gameStart: boolean) => void;
  setScene: (scene: Scene) => void;
  workspace: Blockly.WorkspaceSvg;
  flyoutWidth: number;
  blocklyTopOffset: number;
  event: string;
  setLoading: (loading: boolean) => void;
}) => {
  const {
    stage,
    step,
    reload,
    setReload,
    isFinished,
    scene,
    gameStart,
    setGameStart,
    setScene,
    event,
    workspace,
    flyoutWidth,
    blocklyTopOffset,
    setLoading,
  } = props;
  const play = usePlaySound();
  const navigate = useNavigate();
  const gameSound = useGameSound();
  const { scale, width, height } = useScale();
  const dispatch = useDispatch<AppDispatch>();
  const [totalGem, setTotalGem] = useState(0);
  const [gemScore, setGemScore] = useState(0);
  const [success, setSuccess] = useState(false);
  const stepMap = StageMaps[stage - 1][step - 1];
  const [gameOver, setGameOver] = useState(false);
  const [successShow, setSuccessShow] = useState(false);
  const summary = useSelector((state: RootState) => state.courses.progresses.algorithm.summary);
  const user = useSelector((state: RootState) => state.user.active);
  const [character, setCharacter] = useState<AbstractMesh | null>(null);

  useEffect(() => {
    if (gameOver) {
      recordCourseLevelRun({ success, blockCount: workspace.getAllBlocks(false).length });
      workspace.getAllBlocks(false).forEach((block: Blockly.BlockSvg) => {
        block.setMovable(true);
        block.setEditable(true);
      });
      if (success) {
        setSuccessShow(true);

        gameSound.levelclearSound();
        gameSound.levelclearCheersSound();

        const xml = Blockly.Xml.workspaceToDom(workspace);
        const answer = Blockly.Xml.domToText(xml);

        dayjs.extend(utc);
        dayjs.extend(timezone);

        if (!isFinished) {
          const now = dayjs().tz("Asia/Tokyo").format("YYYY-MM-DDTHH:mm:ssZ"); // 現状は強制的にJSTにする、今後UTCにする
          dispatch(
            actions.updateAlgorithmLevelProgresses({
              stageId: stage,
              stepId: step,
              totalFinished: summary.finished + 1,
              answer,
              finishedAt: now,
            }),
          );
          updateAlgorithmLevelProgresses({
            uid: user.uid,
            stageId: stage,
            stepId: step,
            totalFinished: summary.finished + 1,
            answer,
            finishedAt: now,
          });
        } else {
          dispatch(
            actions.updateAlgorithmLevelAnswer({
              stageId: stage,
              stepId: step,
              answer,
            }),
          );
          updateAlgorithmLevelAnswer({
            uid: user.uid,
            stageId: stage,
            stepId: step,
            answer,
          });
        }
      } else {
        gameSound.failureSound();
      }
    }
  }, [gameOver]);

  useEffect(() => {
    gameSound.itemGetSound();
  }, [gemScore]);

  const handlePlusGemScore = (scene: Scene, totalGem: number) => {
    setGemScore((prevScore) => {
      const newScore = prevScore + 1;
      if (newScore === totalGem) {
        setSuccess(true);
        scene.metadata = { result: true };
      }
      return newScore;
    });
  };

  const highlightBlock = (id: string, opt_state: boolean) => {
    if (!scene.isDisposed) {
      if (!workspace.getBlockById(id)) {
        const xml = Blockly.Xml.workspaceToDom(workspace);
        const xml_text = Blockly.Xml.domToText(xml);
        console.error(
          `failed to get block id: ${id} stage:${stage}, step:${step}, xml_text: ${xml_text}`,
        );
      } else {
        workspace.getBlockById(id).setHighlighted(opt_state);
      }
    }
  };

  const handleStart = () => {
    btnDelay(() => {
      gameSound.startSound();
      setGameStart(true);
      scene.metadata = { result: false };

      workspace.getAllBlocks(false).forEach((block: Blockly.BlockSvg) => {
        block.setMovable(false);
        block.setEditable(false);
      });
      try {
        const handleEvent = new Function(
          "highlightBlock",
          "sleep",
          "scene",
          "character",
          "stepMap",
          "offset",
          "performMove",
          "performJump",
          "performTurnToLeft",
          "performTurnToRight",
          "moveSound",
          "jumpSound",
          "turnSound",
          "checkBtnColor",
          "setGameOver",
          "setSuccess",
          event
            ? event
            : `return () => {
            setGameOver(true);
          }
          `,
        );
        handleEvent(
          highlightBlock,
          sleep,
          scene,
          character,
          stepMap,
          offset,
          performMove,
          performJump,
          performTurnToLeft,
          performTurnToRight,
          gameSound.moveSound,
          gameSound.jumpSound,
          gameSound.turnSound,
          checkBtnColor,
          setGameOver,
          setSuccess,
        )()?.catch((error: unknown) => {
          // 実行中のエラーは、失敗として終わらせる
          console.error(`failed to run event: stage:${stage}, step:${step}, error: ${error}`);
          setSuccess(false);
          setGameOver(true);
        });
      } catch (error) {
        const xml = Blockly.Xml.workspaceToDom(workspace);
        const xml_text = Blockly.Xml.domToText(xml);
        console.error(
          `failed to run event: stage:${stage}, step:${step}, error: ${error}, xml_text: ${xml_text}`,
        );
        setSuccess(false);
        setGameOver(true);
      }
    });
  };

  const handleReset = () => {
    btnDelay(() => {
      if (scene && !scene.isDisposed) {
        scene.dispose();
      }
      setGemScore(0);
      setLoading(true);
      setSuccess(false);
      setGameOver(false);
      setReload(!reload);
      setGameStart(false);
      setSuccessShow(false);
      workspace.getAllBlocks(false).forEach((block: Blockly.BlockSvg) => {
        block.setHighlighted(false);
        block.setMovable(true);
        block.setEditable(true);
      });
    });
  };

  const handleClearBlock = () => {
    play();
    if (gameStart) {
      if (scene && !scene.isDisposed) {
        scene.dispose();
      }
      setGemScore(0);
      setLoading(true);
      setSuccess(false);
      setGameOver(false);
      setReload(!reload);
      setGameStart(false);
      setSuccessShow(false);
    }
    workspace.getAllBlocks(false).forEach((block: Blockly.BlockSvg) => {
      if (block.type === "character_event") {
        block.setHighlighted(false);
        block.moveTo(new Blockly.utils.Coordinate(flyoutWidth / scale + 20, blocklyTopOffset));
      } else {
        block.dispose();
      }
    });
  };

  const handleBack = () => {
    play();
    btnDelay(() => {
      setSuccessShow(false);
      navigate(`/courses/${Course.ALGORITHM}/stage/${stage}`);
    });
  };

  return (
    <>
      {gameOver && !success && <Failed scale={scale} reset={handleReset} />}

      {successShow && <Success scale={scale} back={handleBack} reset={handleReset} />}

      <div className="flex-col-view items-center w-full h-full pointer-events-none">
        <div className="flex-col-center w-full h-full pointer-events-none">
          <GameScene
            width={width}
            height={height}
            scale={scale}
            stage={stage}
            step={step}
            map={stepMap}
            reload={reload}
            handler={{
              setLoading,
              setScene,
              setCharacter,
              setTotalGem,
              handlePlusGemScore,
            }}
          />

          {/* てにいれたアイテム */}
          <div
            className="flex-row-center !items-start w-full !absolute top-0"
            style={{
              height: 56 * scale,
              left: -(flyoutWidth + 10 * scale) / 2,
            }}
          >
            <div
              className="flex-row-view h-[56px] origin-top"
              style={{ transform: `scale(${scale})` }}
            >
              <Score totalGem={totalGem} gemScore={gemScore} />
            </div>
          </div>
        </div>

        {!gameStart && (
          <div
            className="flex-col-center !absolute z-50 origin-bottom-left pointer-events-auto"
            style={{
              bottom: 24 * scale,
              left: -flyoutWidth - 88 * scale,
              transform: `scale(${scale})`,
            }}
          >
            <button
              className="opacity-button pointer-events-auto bg-gray-80"
              onClick={handleClearBlock}
            >
              <div className="flex-col-center w-[48px] h-[48px]">
                <ResetBtnIcon />
              </div>
            </button>
          </div>
        )}

        {!gameStart ? (
          <StartBtn
            scale={scale}
            disabled={
              workspace.getBlocksByType("character_event", false).length < 1 ||
              workspace.getBlocksByType("character_event", false).shift().getChildren(false)
                .length < 1
            }
            start={handleStart}
          />
        ) : (
          <ResetBtn scale={scale} reset={handleReset} />
        )}
      </div>
    </>
  );
};

export default MainScenePage;
